import React, { useMemo, useCallback, useState, FC, useRef, useContext } from 'react';
import { ThemeProvider } from 'styled-components';
import hoistNonReactStatics from 'hoist-non-react-statics';

import { Theme } from 'ia-react-core/~/interfaces/Theme';
import keys from 'ia-react-core/~/utils/accessors/keys';
import BREAKPOINTS from 'ia-react-core/~/constants/BREAKPOINTS';
import { DeepWriteable } from 'ia-react-core/~/interfaces/DeepWriteable';
import useResize from 'ia-react-core/~/hooks/#useResize';
import getAppContainerId from 'ia-react-core/~/utils/dom/getAppContainerId';
import RootContext from 'ia-react-core/~/components/config/#RootContext';
import getWrappedDisplayName from '../../../utils/hocs/getWrappedDisplayName';

export interface WithThemingProps {
  theme?: Theme;
}

export interface WithThemingOptions {
  compensateBreakpoints?: boolean;
  compensationRatio?: number;
}

export interface WithThemingMock {}

/**
 * @since 0.1.0
 * @link See [Theming](/Devguide/Coding/Theming)
 * @see See [App configuration](/Devguide/App%20configuration)
 * @component
 */
export default function withTheming(theme: Theme | ((dynamicTheme: Theme) => Theme), { compensateBreakpoints = true, compensationRatio = 1 }: WithThemingOptions = {}) {
  return (WrappedComponent: FC<WithThemingProps>) => {
    const WithTheming = ({ theme: dynamicTheme, ...props }: WithThemingProps) => {
      const { root } = useContext(RootContext);
      const rootNode = useMemo(() => root.querySelector(`#${getAppContainerId()}`) || document.body, [root]);

      const resolvedTheme = useMemo(() => {
        if (typeof theme === 'function') {
          return theme(dynamicTheme);
        }

        if (theme) {
          return theme;
        }

        return dynamicTheme;
      }, [dynamicTheme]);

      const getUpdatedBreakpoints = useCallback((width: number, currentBreakpoints: typeof BREAKPOINTS) => (
        keys(currentBreakpoints).reduce((result, breakpoint) => {
          result[breakpoint] = [...currentBreakpoints[breakpoint]];
          const start = currentBreakpoints[breakpoint][0];
          const end = currentBreakpoints[breakpoint][1];
          if (start) result[breakpoint][0] = start + width;
          if (end) result[breakpoint][1] = end + width;
          return result;
        }, {} as typeof BREAKPOINTS)
      ), []);

      const [compensatedBreakpoints, setCompensatedBreakpoints] = useState(() => {
        const width = document.body.clientWidth - (rootNode.clientWidth / compensationRatio);
        const startBreakpoints = getUpdatedBreakpoints(width, resolvedTheme.breakpoints);
        return startBreakpoints;
      });

      const finalTheme = useMemo(() => ({
        ...resolvedTheme,
        breakpoints: compensatedBreakpoints,
      }), [compensatedBreakpoints, resolvedTheme]);

      if ((process.env.NODE_ENV === 'development' || process.env.REACT_APP_IA_DEV_TOOLS_INCLUDE_BUILD === 'true')) {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const rootNodeRef = useRef(rootNode);
        // eslint-disable-next-line react-hooks/rules-of-hooks
        useResize(rootNodeRef, () => {
          const baseBreakoints = resolvedTheme.breakpoints as DeepWriteable<typeof resolvedTheme.breakpoints>;

          const width = document.body.clientWidth - rootNode.clientWidth;
          if (!width) {
            setCompensatedBreakpoints(baseBreakoints);
          } else {
            const updatedBreakpoints = keys(baseBreakoints).reduce((result, breakpoint) => {
              result[breakpoint] = [...baseBreakoints[breakpoint]];
              const start = baseBreakoints[breakpoint][0];
              const end = baseBreakoints[breakpoint][1];
              if (start)result[breakpoint][0] = start + width;
              if (end) result[breakpoint][1] = end + width;
              return result;
            }, {} as typeof BREAKPOINTS);
            setCompensatedBreakpoints(updatedBreakpoints);
          }
        });
      }

      return (
        <ThemeProvider theme={compensateBreakpoints ? finalTheme : resolvedTheme}>
          <WrappedComponent {...props} />
        </ThemeProvider>
      );
    };

    WithTheming.displayName = getWrappedDisplayName('WithTheming', WrappedComponent);

    return hoistNonReactStatics(WithTheming, WrappedComponent);
  };
}
